// infrastructure/api/appointmentApi.js

import { Timestamp } from 'firebase/firestore';
import { useTime } from 'src/composables/time';
import AppointmentsDB from 'src/fb/appointments-db';
import GenericDb from 'src/fb/generic-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';

import {
  assertLinkable,
  buildAppointmentLinkUpdate,
  buildSessionActivityLinkUpdate,
  extractAppointmentLinkData,
} from '../../domain/services/appointmentLinker';

/**
 * Fetches appointments that are candidates for linking to a session activity.
 * Returns only scheduled appointments on the same day that are not yet linked to any activity.
 *
 * @param {{
 *   clinicId: string,
 *   userId: string,
 *   clientId: string,
 *   date: import('firebase/firestore').Timestamp,
 * }} params
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal] - Optional signal to cancel the request.
 * @returns {Promise<Object[]>} Appointments matching the given criteria.
 */
// eslint-disable-next-line import/prefer-default-export
export async function loadPotentialAppointments(params, options = {}) {
  const { signal } = options;

  // If already aborted, fail fast without making the call.
  if (signal?.aborted) {
    throw new DOMException('Request cancelled', 'AbortError');
  }

  const { endOfDate, startOfDate } = useTime();

  // Get the start of the day for the session activity's date
  const activityDate = params.date.toDate();
  const fromDate = startOfDate(activityDate, 'day');

  const fromTimestamp = Timestamp.fromDate(fromDate);

  // Get the end of the day for the session activity's date
  const toDate = endOfDate(activityDate, 'day');
  const toTimestamp = Timestamp.fromDate(toDate);

  const query = [
    ['clinic', '==', params.clinicId],
    ['client', '==', params.clientId],
    ['user', '==', params.userId],
    ['status', '==', 'scheduled'],
    ['scheduledStartTimestamp', '>=', fromTimestamp],
    ['scheduledStartTimestamp', '<', toTimestamp],
  ];

  const appointmentsDb = new AppointmentsDB();
  const appointments = await appointmentsDb.readAll(query);
  return appointments
    .filter((appt) => (appt.sessionActivity === null || appt.sessionActivity === undefined));
} // loadPotentialAppointments()

/**
 * Atomically links a session activity to an appointment.
 * Verifies neither document is already linked before writing.
 *
 * @param {string} sessionActivityId
 * @param {string} appointmentId
 * @returns {Promise<void>}
 */
export async function linkSessionActivityToAppointment(sessionActivityId, appointmentId) {
  const appointmentsDb = new AppointmentsDB();
  const sessionActivitiesDb = new SessionActivitiesDB();
  const genericDb = new GenericDb();

  await genericDb.executeTransaction(async (t) => {
    const [appointment, sessionActivity] = await Promise.all([
      t.read(appointmentsDb, { id: appointmentId }),
      t.read(sessionActivitiesDb, { id: sessionActivityId }),
    ]);

    if (!appointment) {
      throw new Error(`Appointment ${appointmentId} not found`);
    }
    if (!sessionActivity) {
      throw new Error(`Session activity ${sessionActivityId} not found`);
    }

    assertLinkable(appointment, sessionActivity, appointmentId, sessionActivityId);

    const appointmentData = extractAppointmentLinkData(appointment);

    t.set(appointmentsDb, {
      data: buildAppointmentLinkUpdate(sessionActivityId, sessionActivity),
      id: appointmentId,
    });

    t.set(sessionActivitiesDb, {
      data: buildSessionActivityLinkUpdate(appointmentId, appointmentData),
      id: sessionActivityId,
    });
  });
} // linkSessionActivityToAppointment()
